import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Chip,
  IconButton,
  Grid,
} from "@mui/material";
import { Add as AddIcon, Close as CloseIcon, Edit as EditIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import AddPayrollInputTemplateDialog from "components/AddPayrollInputTemplateDialog";
import AddTemplateDetailDialog from "components/AddTemplateDetailDialog";
import SnackbarWrapper from "components/Snackbar";
import ConfirmationDialog from "components/ConfirmationDialog";
import LoadingScreen from "components/Loading";
import {
  getInputTemplates,
  addInputTemplate,
  updateInputTemplate,
  deleteInputTemplate,
  getTemplateDetails,
  addTemplateDetail,
  deleteTemplateDetail,
  getActiveSalaryComponents,
  getJobs,
} from "services/api";
import MDButton from "components/MDButton";

const TemplatesView = ({ canEdit }) => {
  const { t } = useTranslation();
  const snackbarRef = useRef();

  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [availableComponents, setAvailableComponents] = useState([]);

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateDetails, setTemplateDetails] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [templateBeingEdited, setTemplateBeingEdited] = useState(null);

  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      const [templatesRes, jobsRes, componentsRes] = await Promise.all([
        getInputTemplates(),
        getJobs(),
        getActiveSalaryComponents(),
      ]);
      if (templatesRes !== "error") setTemplates(templatesRes);
      if (jobsRes !== "error") setJobs(jobsRes);
      if (componentsRes !== "error") setAvailableComponents(componentsRes);
      setLoading(false);
    };
    loadAll();
  }, []);

  const loadDetailsForTemplate = async (templateId) => {
    setDetailsLoading(true);
    const res = await getTemplateDetails(templateId);
    if (res !== "error") {
      setTemplateDetails(res);
    } else {
      snackbarRef.current?.showSnackbar(t("admin.payroll.templates.loadDetailsError"), "error");
    }
    setDetailsLoading(false);
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    loadDetailsForTemplate(template.id);
  };

  const handleOpenAdd = () => {
    setTemplateBeingEdited(null);
    setEditMode(false);
    setTemplateDialogOpen(true);
  };

  const handleOpenEdit = (template, event) => {
    event.stopPropagation();
    setTemplateBeingEdited(template);
    setEditMode(true);
    setTemplateDialogOpen(true);
  };

  const handleSubmitTemplate = async (form) => {
    if (editMode) {
      const res = await updateInputTemplate(templateBeingEdited.id, form);
      if (res !== "error") {
        setTemplates(templates.map((tpl) => (tpl.id === res.id ? res : tpl)));
        if (selectedTemplate?.id === res.id) setSelectedTemplate(res);
        snackbarRef.current?.showSnackbar(t("admin.payroll.templates.updateSuccess"), "success");
      } else {
        snackbarRef.current?.showSnackbar(t("admin.payroll.templates.updateError"), "error");
      }
    } else {
      const res = await addInputTemplate(form);
      if (res !== "error") {
        setTemplates([...templates, res]);
        snackbarRef.current?.showSnackbar(t("admin.payroll.templates.addSuccess"), "success");
      } else {
        snackbarRef.current?.showSnackbar(t("admin.payroll.templates.addError"), "error");
      }
    }
    setTemplateDialogOpen(false);
  };

  const handleDeleteClick = () => {
    setTemplateDialogOpen(false);
    setTemplateToDelete(templateBeingEdited);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    const res = await deleteInputTemplate(templateToDelete.id);
    if (res === "success") {
      setTemplates(templates.filter((tpl) => tpl.id !== templateToDelete.id));
      if (selectedTemplate?.id === templateToDelete.id) {
        setSelectedTemplate(null);
        setTemplateDetails([]);
      }
      snackbarRef.current?.showSnackbar(t("admin.payroll.templates.deleteSuccess"), "success");
    } else if (typeof res === "string" && res.startsWith("Cannot delete")) {
      snackbarRef.current?.showSnackbar(t("admin.payroll.templates.cannotDelete"), "error");
    } else {
      snackbarRef.current?.showSnackbar(t("admin.payroll.templates.deleteError"), "error");
    }
    setConfirmOpen(false);
  };

  const handleAddDetail = async (payload) => {
    const res = await addTemplateDetail({
      ...payload,
      payrollInputTemplateId: selectedTemplate.id,
    });
    if (res !== "error" && typeof res !== "string") {
      setTemplateDetails([...templateDetails, res]);
      snackbarRef.current?.showSnackbar(t("admin.payroll.templates.detailAdded"), "success");
    } else {
      snackbarRef.current?.showSnackbar(
        typeof res === "string" ? res : t("admin.payroll.templates.detailAddError"),
        "error"
      );
    }
  };

  const handleRemoveDetail = async (detailId) => {
    const res = await deleteTemplateDetail(detailId);
    if (res === "success") {
      setTemplateDetails(templateDetails.filter((d) => d.id !== detailId));
      snackbarRef.current?.showSnackbar(t("admin.payroll.templates.detailRemoved"), "success");
    } else {
      snackbarRef.current?.showSnackbar(t("admin.payroll.templates.detailRemoveError"), "error");
    }
  };

  const componentName = (salaryComponentId) => {
    const match = availableComponents.find((c) => c.id === salaryComponentId);
    return match ? match.name : `#${salaryComponentId}`;
  };

  const jobName = (jobId) => {
    if (!jobId) return t("admin.payroll.templates.noJobLink");
    const match = jobs.find((j) => j.jobId === jobId);
    return match ? match.jobName : `#${jobId}`;
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Left panel - templates list */}
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <MDBox
              sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}
            >
              <MDTypography variant="subtitle2" color="text">
                {t("admin.payroll.templates.title")}
              </MDTypography>
              {canEdit && (
                <MDButton size="small" startIcon={<AddIcon />} onClick={handleOpenAdd}>
                  {t("admin.payroll.templates.addButton")}
                </MDButton>
              )}
            </MDBox>

            <List dense>
              {templates.map((template) => (
                <ListItemButton
                  key={template.id}
                  selected={selectedTemplate?.id === template.id}
                  onClick={() => handleSelectTemplate(template)}
                  sx={{ borderRadius: 1 }}
                >
                  <ListItemText
                    primary={template.name}
                    secondary={jobName(template.jobId)}
                    secondaryTypographyProps={{ variant: "caption" }}
                  />
                  {canEdit && (
                    <IconButton size="small" onClick={(e) => handleOpenEdit(template, e)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Right panel - components for the selected template */}
        <Grid item xs={12} md={8}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            {selectedTemplate ? (
              <>
                <MDBox
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 2,
                  }}
                >
                  <MDBox>
                    <MDTypography variant="h6">{selectedTemplate.name}</MDTypography>
                    <MDTypography variant="caption" color="text">
                      {jobName(selectedTemplate.jobId)}
                    </MDTypography>
                  </MDBox>
                  {canEdit && (
                    <MDButton
                      size="small"
                      color="primary"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => setDetailDialogOpen(true)}
                    >
                      {t("admin.payroll.templates.addDetail")}
                    </MDButton>
                  )}
                </MDBox>

                {detailsLoading ? (
                  <LoadingScreen />
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            {t("admin.payroll.templates.component")}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: "bold" }}>
                            {t("admin.payroll.templates.defaultAmount")}
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: "bold" }}>
                            {t("admin.payroll.templates.required")}
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: "bold" }}>
                            {t("admin.payroll.templates.editable")}
                          </TableCell>
                          <TableCell align="right"></TableCell>
                        </TableRow>
                        {templateDetails.map((detail) => (
                          <TableRow key={detail.id}>
                            <TableCell>{componentName(detail.salaryComponentId)}</TableCell>
                            <TableCell align="right">
                              {detail.defaultAmount?.toLocaleString()}
                            </TableCell>
                            <TableCell align="center">
                              {detail.required && (
                                <Chip
                                  label={t("admin.payroll.templates.requiredChip")}
                                  color="warning"
                                  size="small"
                                />
                              )}
                            </TableCell>
                            <TableCell align="center">{detail.editable ? "✓" : "—"}</TableCell>
                            <TableCell align="right">
                              {canEdit && (
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleRemoveDetail(detail.id)}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            ) : (
              <MDBox sx={{ py: 4, textAlign: "center" }}>
                <MDTypography color="text">
                  {t("admin.payroll.templates.selectTemplate")}
                </MDTypography>
              </MDBox>
            )}
          </Paper>
        </Grid>
      </Grid>

      <AddPayrollInputTemplateDialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        onSubmit={handleSubmitTemplate}
        template={templateBeingEdited}
        editMode={editMode}
        handleDelete={handleDeleteClick}
        jobs={jobs}
      />

      <AddTemplateDetailDialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        onSubmit={handleAddDetail}
        availableComponents={availableComponents.filter(
          (c) => !templateDetails.some((d) => d.salaryComponentId === c.id)
        )}
      />

      <ConfirmationDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        message={t("admin.payroll.templates.confirmDelete", { name: templateToDelete?.name })}
      />

      <SnackbarWrapper
        ref={snackbarRef}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      />
    </Box>
  );
};

TemplatesView.propTypes = {
  canEdit: PropTypes.bool.isRequired,
};

export default TemplatesView;
